// HTML-string building blocks for save analyzer reports (styles in _game_tool_kit.scss).
// Arguments named `html` or `body` are trusted markup; every other string is escaped here.
import { esc, img } from "./html"
import { percent, fraction } from "./format"

export function count(text) {
  return `<span class="game-tool__panel-count">${text}</span>`
}

export function panel(title, body, extraClass = "", { hideTitle = false, id = "" } = {}) {
  return `<section${id ? ` id="${id}" tabindex="-1"` : ""} class="game-tool__panel game-tool__section${extraClass ? ` ${extraClass}` : ""}">` +
    `<h2 class="game-tool__panel-title${hideTitle ? " visually-hidden" : ""}">${title}</h2>${body}</section>`
}

export function bar(done, total, label) {
  const p = percent(done, total)
  return `<div class="game-tool__bar" role="progressbar" aria-valuenow="${p}" aria-valuemin="0" aria-valuemax="100" aria-label="${esc(label)}">` +
    `<span style="width:${p}%"></span></div>`
}

/** A bar that repeats numbers already shown beside it, so it's hidden from screen readers. */
export function decorativeBar(done, total) {
  return `<span class="game-tool__bar" aria-hidden="true"><span style="width:${percent(done, total)}%"></span></span>`
}

/**
 * Panel led by a progress bar, then the count beside any controls. The tab already names
 * the panel, so its heading is kept only for screen readers and the heading outline.
 */
export function tallyPanel(label, have, total, body, controls = "") {
  return panel(esc(label), tallyBody(label, have, total, body, controls), "", { hideTitle: true })
}

export function tallyBody(label, have, total, body, controls = "") {
  return `
    ${bar(have, total, label)}
    <div class="game-tool__collection-controls">${count(fraction(have, total))}${controls}</div>
    ${body}`
}

/** One entry in a `<dl class="game-tool__stat-grid">`; `valueHtml` and `noteHtml` are markup. */
export function stat(label, valueHtml, noteHtml = "", icon = "") {
  return `<div class="game-tool__stat${icon ? " has-icon" : ""}">${img(icon, "", "game-tool__stat-icon")}<dt>${esc(label)}</dt>` +
    `<dd>${valueHtml}${noteHtml ? `<small>${noteHtml}</small>` : ""}</dd></div>`
}

/**
 * An on/off setting. `setting` becomes data-setting, which the controller's change handler reads.
 * `context` is read after the label by screen readers only, to tell same-named switches apart.
 */
export function toggle(label, setting, checked, { small = false, context = "" } = {}) {
  return `<label class="game-tool__toggle${small ? " game-tool__toggle--small" : ""}">` +
    `<input type="checkbox" role="switch" data-setting="${setting}" ${checked ? "checked" : ""}> ${esc(label)}` +
    `${context ? `<span class="visually-hidden">, ${esc(context)}</span>` : ""}</label>`
}

export function emptyState(text) {
  return `<p class="game-tool__muted">${esc(text)}</p>`
}

// --- Found / equipped marks --------------------------------------------------

export const OWNED_MARKS = {
  equipped: { icon: "fa-gears", label: "Equipped" },
  found: { icon: "fa-check", label: "Found" },
}

/**
 * Corner mark on a found item card: gears if equipped, a check otherwise. `leadHtml` goes before the
 * icon; `icon` swaps it for games with more states than found / equipped.
 */
export function ownedTag(isEquipped, { label = OWNED_MARKS[isEquipped ? "equipped" : "found"].label, icon = "", leadHtml = "" } = {}) {
  const key = isEquipped ? "equipped" : "found"
  return `<span class="game-tool__owned-tag is-${key}">${leadHtml}<i class="fa-solid ${icon || OWNED_MARKS[key].icon}" aria-hidden="true"></i><span class="visually-hidden">${label}</span></span>`
}

/** A key to card marks: each item is { iconHtml, label, className }; `iconHtml` should be hidden from screen readers. */
export function markKey(items) {
  const rows = items.map(({ iconHtml, label, className = "" }) =>
    `<li${className ? ` class="${className}"` : ""}>${iconHtml}${esc(label)}</li>`)
  return `<ul class="game-tool__owned-key" aria-label="Key">${rows.join("")}</ul>`
}

/** Key to the ownedTag marks, then any `extra` markKey items. */
export function ownedKey(extra = []) {
  const marks = Object.entries(OWNED_MARKS).map(([key, { icon, label }]) =>
    ({ iconHtml: `<i class="fa-solid ${icon}" aria-hidden="true"></i>`, label, className: `is-${key}` }))
  return markKey([...marks, ...extra])
}

export function checkIcon(checked) {
  return `<i class="fa-regular ${checked ? "fa-square-check" : "fa-square"} game-tool__check" aria-hidden="true"></i>`
}

/** Row of a `<ul class="game-tool__checklist">`; `state` is the announced word, `lead` sits before the text and `trail` after it. */
export function checkRow(checked, html, state, { lead = "", trail = "" } = {}) {
  return `<li${checked ? ' class="is-done"' : ""}>${checkIcon(checked)}${lead}` +
    `<span>${html}<span class="visually-hidden">, ${state}</span></span>${trail}</li>`
}

// --- Completion tree ---------------------------------------------------------
// A `<ul class="game-tool__completion">` of completionRows. A row with a `body` folds out; the
// body holds more rows (`<ul class="game-tool__completion-kinds">`, `… is-sub` one level down) or
// completionEntry lines (`<ul class="game-tool__completion-entries">`).

/**
 * One counted row. `top` rows (the outer list) add the percentage. With a `body` the row is a
 * disclosure remembered by `key` (data-collapse); without one it's a leaf with a labelled bar.
 * `labelHtml` overrides the escaped `label`; `showBar: false` drops the bar from a disclosure;
 * `open` renders the disclosure expanded.
 */
export function completionRow({ label, labelHtml = esc(label), done, total, body = "", key = "", top = false, showBar = true, open = false, className = "" }) {
  const numbers = `${fraction(done, total)}${top ? ` · ${percent(done, total)}%` : ""}`
  const head = `<span class="game-tool__completion-head"><span>${labelHtml}</span><span>${numbers}</span></span>`
  const classes = [className, done >= total ? "is-done" : ""].filter(Boolean).join(" ")
  if (!body) return `<li class="is-leaf${classes ? ` ${classes}` : ""}">${head}${bar(done, total, label)}</li>`
  // The summary's bar repeats its numbers, so it's hidden from screen readers.
  return `<li class="${classes}"><details class="game-tool__completion-area${top ? "" : " is-kind"}" data-collapse="${esc(key)}"${open ? " open" : ""}>` +
    `<summary>${head}${showBar ? decorativeBar(done, total) : ""}</summary>${body}</details></li>`
}

/** One line under a completion row: a check, what it is (`html`), then optional `trail` markup. */
export function completionEntry(done, html, { trail = "" } = {}) {
  return `<li class="is-line${done ? " is-done" : ""}"><span>${checkIcon(done)} ${html}` +
    `<span class="visually-hidden">, ${done ? "done" : "not done"}</span></span>${trail}</li>`
}

/** Expand all / Collapse all; the controller opens or closes every `details` in the tree. */
export function completionSectionButtons() {
  return `<div class="game-tool__btn-group" role="group" aria-label="Completion sections">
    <button type="button" class="game-tool__btn game-tool__btn--ghost game-tool__btn-small" data-completion-sections="open">Expand all</button>
    <button type="button" class="game-tool__btn game-tool__btn--ghost game-tool__btn-small" data-completion-sections="close">Collapse all</button>
  </div>`
}

// --- Collection filters ------------------------------------------------------

/** The standard filter set. `needsSpoilers` options are disabled until spoilers are on. */
export const COLLECTION_FILTERS = [
  { id: "all", label: "All" },
  { id: "equipped", label: "Equipped", icon: OWNED_MARKS.equipped.icon },
  { id: "found", label: "Found", icon: OWNED_MARKS.found.icon },
  { id: "missing", label: "Missing", icon: "fa-circle-exclamation", needsSpoilers: true },
]

/** The filter in effect: `current` if it's valid and allowed, otherwise the first. */
export function activeFilter(filters, current, spoilers) {
  const f = filters.find((x) => x.id === current) || filters[0]
  return f.needsSpoilers && !spoilers ? filters[0] : f
}

/** A predicate on item ids for one filter id. */
export function filterKeep(filterId, owned, equipped) {
  return {
    all: () => true,
    equipped: (id) => equipped.has(id),
    found: (id) => owned.has(id),
    missing: (id) => !owned.has(id),
  }[filterId]
}

export function filterEmptyText(filterId, noun) {
  return {
    all: `No ${noun} found yet.`,
    equipped: `No ${noun} equipped.`,
    found: `No ${noun} found yet.`,
    missing: `You've found all the ${noun}.`,
  }[filterId]
}

/**
 * Segmented radio group. Each radio carries data-setting="filter" data-filter=`key`, so one
 * change handler serves every group. `prefix` keeps names and ids unique per tool.
 */
export function filterGroup({ prefix, key, noun, filters = COLLECTION_FILTERS, current, spoilers }) {
  const selected = activeFilter(filters, current, spoilers).id
  const hintId = `${prefix}-${key}-filter-hint`
  const options = filters.map((f) => {
    const disabled = f.needsSpoilers && !spoilers
    return `<label class="game-tool__filter-option${disabled ? " is-disabled" : ""}">` +
      `<input type="radio" name="${prefix}-${key}-filter" value="${f.id}" data-setting="filter" data-filter="${key}"${f.id === selected ? " checked" : ""}${disabled ? ` disabled aria-describedby="${hintId}"` : ""}>` +
      `${f.icon ? `<i class="fa-solid ${f.icon}" aria-hidden="true"></i>` : ""}${f.label}</label>`
  })
  return `
      <fieldset class="game-tool__filter">
        <legend class="visually-hidden">Show ${noun}</legend>
        ${options.join("")}
      </fieldset>
      ${spoilers || !filters.some((f) => f.needsSpoilers) ? "" : `<span id="${hintId}" class="game-tool__hint">Turn on spoilers to list missing ${noun}.</span>`}`
}

/**
 * A numbered collection's panel: a bar, `introHtml`, then each entry's label plus a link to where it
 * is. `groups` is [{ heading?, entries }] (heading is markup); an entry is { found, label, link?, lead? }
 * with `label` text, `link` from locationLink and `lead` an icon. Without spoilers, missing entries
 * read ???. `states` names an entry each way (e.g. ["released", "not released"]); `columns` (2 or 3)
 * splits long lists. `layout: "boxes"` draws each entry as a small box with the owned mark in its
 * corner instead of a row, sized for a bare number like "#3"; "named-boxes" makes them wider for
 * labels with words. `outroHtml` follows the lists. `id` makes the panel an in-page link target.
 */
export function locationChecklist({ titleHtml, title, groups, spoilers, introHtml = "", outroHtml = "", states = ["found", "missing"], columns = 1, layout = "rows", id = "" }) {
  const [yes, no] = states
  const entries = groups.flatMap((group) => group.entries)
  const done = entries.filter((entry) => entry.found).length
  const row = ({ found, label, link = "", lead = "" }) => (!found && !spoilers
    ? checkRow(false, "???", no)
    : checkRow(found, esc(label), found ? yes : no, { lead, trail: link }))
  const box = ({ found, label, link = "", lead = "" }) => {
    const shown = found || spoilers
    return `<li class="game-tool__location-box${found ? " is-done" : ""}">${shown ? lead : ""}` +
      `<span>${shown ? esc(label) : "???"}${found ? "" : `<span class="visually-hidden">, ${no}</span>`}</span>` +
      `${found ? ownedTag(false, { label: yes }) : ""}${shown ? link : ""}</li>`
  }
  const list = layout === "boxes" || layout === "named-boxes"
    ? (items) => `<ul class="game-tool__location-boxes${layout === "named-boxes" ? " game-tool__location-boxes--named" : ""}">${items.map(box).join("")}</ul>`
    : (items) => `<ul class="game-tool__checklist${columns > 1 ? ` game-tool__checklist--cols-${columns}` : ""}">${items.map(row).join("")}</ul>`
  const lists = groups.map(({ heading, entries: items }) =>
    `${heading ? `<h3 class="game-tool__subheading">${heading}</h3>` : ""}${list(items)}`)
  return panel(`${titleHtml} ${count(fraction(done, entries.length))}`,
    `${bar(done, entries.length, `${title} ${yes}`)}${introHtml}${lists.join("")}${outroHtml}`, "", { id })
}

// --- Item cards --------------------------------------------------------------

/** Off-site "where to find it" link. Names the item and the site, and says it opens a new tab. */
export function locationLink({ url, item, site }) {
  if (!url) return ""
  return `<a class="game-tool__location-link" href="${esc(url)}" target="_blank" rel="noopener">` +
    `<i class="fa-solid fa-location-dot" aria-hidden="true"></i>` +
    `<span class="visually-hidden">Where to find ${esc(item)} on ${esc(site)} (opens in a new tab)</span></a>`
}

/**
 * One card in a `<ul class="game-tool__item-grid">`. Unfound items show as ??? unless `revealed`
 * (spoilers on). `descriptionHtml` / `loreHtml` are markup; `link` is { url, site } for locationLink.
 * `cornerHtml` sits in the top-left corner (e.g. a cost); the location link takes the bottom-right.
 * `iconHtml` replaces the `icon` image (e.g. a CSS sprite); `showIcon: false` drops the icon box for
 * things that have no art. `lockedIcon` fills the ??? card's icon box (e.g. the game's own
 * placeholder art). `className` adds classes to the `<li>`.
 */
export function itemCard({ name, icon, iconHtml = "", lockedIcon = "", showIcon = true, descriptionHtml = "", loreHtml = "", found, equipped = false, revealed, noun, link = null, tagHtml = null, cornerHtml = "", className = "" }) {
  const extra = className ? ` ${className}` : ""
  if (!found && !revealed) {
    return `<li class="game-tool__item-card is-locked${showIcon ? "" : " is-iconless"}${extra}">${showIcon ? `<div class="game-tool__item-card-icon">${img(lockedIcon)}</div>` : ""}` +
      `<strong>???</strong><span class="visually-hidden">Undiscovered ${noun}</span></li>`
  }
  return `
      <li class="game-tool__item-card${found ? "" : " is-missing"}${equipped ? " is-equipped" : ""}${extra}">
        ${showIcon ? `<div class="game-tool__item-card-icon">${iconHtml || img(icon)}</div>` : ""}
        <strong>${esc(name)}</strong>
        ${found ? tagHtml ?? ownedTag(equipped) : ""}
        ${link ? locationLink({ ...link, item: name }) : ""}
        ${descriptionHtml ? `<p>${descriptionHtml}</p>` : ""}
        ${loreHtml ? `<p class="game-tool__item-card-lore">${loreHtml}</p>` : ""}
        ${cornerHtml ? `<span class="game-tool__item-card-corner">${cornerHtml}</span>` : ""}
      </li>`
}
